import React from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { INR } from "@/lib/constants";
import { TrendingUp, Building2, Dumbbell, Package, IndianRupee, ArrowDownCircle } from "lucide-react";

const VOLT = "#CCFF00";

function Tile({ icon, label, value, sub, testid, color = VOLT }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold flex items-center gap-2">{icon}{label}</div>
      <div className="font-display text-4xl font-black mt-2 leading-none" style={{ color }}>{value}</div>
      {sub ? <div className="text-xs text-zinc-500 mt-1">{sub}</div> : null}
    </div>
  );
}

function BreakdownList({ title, testid, data, emptyText = "No data" }) {
  const entries = Object.entries(data || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const max = entries.length ? entries[0][1] : 1;
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold mb-3">{title}</div>
      {entries.length === 0 ? (
        <div className="text-zinc-600 text-xs py-2">{emptyText}</div>
      ) : (
        <div className="space-y-2">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-center gap-3">
              <div className="w-32 text-xs text-zinc-400 truncate">{k}</div>
              <div className="flex-1 h-2 bg-zinc-900 rounded-sm overflow-hidden">
                <div className="h-full bg-volt transition-all" style={{ width: `${(v / max) * 100}%` }} />
              </div>
              <div className="font-mono-cmd text-xs text-volt w-24 text-right">{INR(v)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RevenueExpenseBreakdown() {
  const { user, activeBranchId } = useAuth();
  const isSuper = user?.role === "SUPER_ADMIN";
  const branchParam = activeBranchId ? `?branch_id=${activeBranchId}` : "";
  const { data: rev } = useQuery({
    queryKey: ["revenue-breakdown", activeBranchId],
    queryFn: async () => (await apiClient.get(`/finance/revenue-breakdown${branchParam}`)).data,
    enabled: user?.role === "SUPER_ADMIN" || user?.role === "ADMIN",
  });
  const { data: exp } = useQuery({
    queryKey: ["expense-breakdown", activeBranchId],
    queryFn: async () => (await apiClient.get(`/finance/expense-breakdown${branchParam}`)).data,
    enabled: user?.role === "SUPER_ADMIN" || user?.role === "ADMIN",
  });
  if (!rev && !exp) return null;

  const netRevenue = (rev?.total_gross || 0) - (exp?.total || 0);

  return (
    <div className="space-y-4" data-testid="rev-exp-breakdown">
      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {isSuper && (
          <Tile
            testid="kpi-total-revenue"
            icon={<IndianRupee size={14} className="text-volt" />}
            label="Total Revenue · MTD"
            value={INR(rev?.total_gross || 0)}
            sub={`${rev?.payment_count || 0} payments · ${rev?.merch_count || 0} merch sales`}
          />
        )}
        <Tile
          testid="kpi-total-expenses"
          icon={<ArrowDownCircle size={14} className="text-red-400" />}
          label="Total Expenses · MTD"
          value={INR(exp?.total || 0)}
          sub={`${INR(exp?.outstanding || 0)} outstanding`}
          color="#FF3B30"
        />
        {isSuper && (
          <Tile
            testid="kpi-net-position"
            icon={<TrendingUp size={14} className="text-green-400" />}
            label="Net Position"
            value={INR(netRevenue)}
            sub="Revenue − Expenses"
            color={netRevenue >= 0 ? "#00E676" : "#FF3B30"}
          />
        )}
        <Tile
          testid="kpi-merch"
          icon={<Package size={14} className="text-volt" />}
          label="Merch Revenue"
          value={INR(rev?.merch_total || 0)}
          sub={`${rev?.merch_count || 0} sales`}
        />
      </div>

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel data-testid="branch-revenue-panel">
          <PanelHeader title="Branch-wise Revenue" subtitle="Gross this month" />
          {(rev?.by_branch || []).length === 0 ? (
            <div className="text-zinc-600 text-xs py-2">No data</div>
          ) : (
            <div className="space-y-2">
              {(rev?.by_branch || []).map((b) => (
                <div key={b.branch_id} className="flex items-center gap-3" data-testid={`branch-rev-${b.branch_id}`}>
                  <Building2 size={12} className="text-zinc-500" />
                  <div className="flex-1 text-xs text-zinc-300">{b.branch_name}</div>
                  <div className="font-mono-cmd text-xs text-volt">{INR(b.gross)}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <Panel data-testid="program-revenue-panel">
          <PanelHeader title="Programme-wise Revenue" subtitle="Strength / MMA / Hyrox / Yoga / Combat / Mobility" />
          <BreakdownList title="" testid="prog-list" data={rev?.by_program} />
        </Panel>
        <Panel data-testid="service-revenue-panel">
          <PanelHeader title="Service-wise Revenue" subtitle="Membership / PT / Trial / Assessment / Day-pass" />
          <BreakdownList title="" testid="svc-list" data={rev?.by_service} />
        </Panel>
      </div>

      {/* Expenses grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel data-testid="branch-expense-panel">
          <PanelHeader title="Branch-wise Expenses" subtitle="This month" />
          {(exp?.by_branch || []).length === 0 ? (
            <div className="text-zinc-600 text-xs py-2">No data</div>
          ) : (
            <div className="space-y-2">
              {(exp?.by_branch || []).map((b) => (
                <div key={b.branch_id} className="flex items-center gap-3" data-testid={`branch-exp-${b.branch_id}`}>
                  <Building2 size={12} className="text-zinc-500" />
                  <div className="flex-1 text-xs text-zinc-300">{b.branch_name}</div>
                  <div className="font-mono-cmd text-xs text-red-400">{INR(b.total)}</div>
                  <div className="font-mono-cmd text-[10px] text-zinc-500 w-24 text-right">{INR(b.outstanding)} outst.</div>
                </div>
              ))}
            </div>
          )}
        </Panel>
        <Panel data-testid="category-expense-panel">
          <PanelHeader title="Expense Categories"
            subtitle="Rent · Maintenance · Salaries · Coach Allocation / Individual Fees · Inventory · Consumables · Misc" />
          <BreakdownList title="" testid="cat-list" data={exp?.by_category} />
        </Panel>
      </div>
    </div>
  );
}
